import { Download, X, CheckSquare } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { SELECT_ALL_CAP } from "../../../store/Archive/archive.store";
import { ZipProgress } from "../use-archive-zip";

interface ArchiveSelectionToolbarProps {
	selectedCount: number;
	/** null when the scope is not counted (organisation-wide). */
	totalCount: number | null;
	capReached: boolean;
	isZipping: boolean;
	progress: ZipProgress | null;
	onSelectAll: () => void;
	onClear: () => void;
	onDownloadZip: () => void;
}

export const ArchiveSelectionToolbar: React.FC<
	ArchiveSelectionToolbarProps
> = ({
	selectedCount,
	totalCount,
	capReached,
	isZipping,
	progress,
	onSelectAll,
	onClear,
	onDownloadZip,
}) => {
	const selectableCount =
		totalCount === null
			? SELECT_ALL_CAP
			: Math.min(totalCount, SELECT_ALL_CAP);
	const canSelectAll = selectedCount < selectableCount;

	return (
		<div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted px-3 py-2">
			<span className="text-sm font-medium text-foreground">
				{selectedCount.toLocaleString("de-DE")} ausgewählt
			</span>

			{capReached && (
				<span className="text-xs text-muted-foreground">
					Maximal {SELECT_ALL_CAP.toLocaleString("de-DE")} Dateien pro
					Download
				</span>
			)}

			{canSelectAll && !isZipping && (
				<Button
					variant="ghost"
					size="sm"
					onClick={onSelectAll}
					aria-label="Alle gefilterten Dateien auswählen"
					data-testid="archive-select-all-button"
				>
					<CheckSquare className="mr-2 h-4 w-4" />
					{totalCount === null
						? "Alle auswählen"
						: totalCount > SELECT_ALL_CAP
							? `Erste ${selectableCount.toLocaleString(
									"de-DE"
								)} auswählen`
							: `Alle ${selectableCount.toLocaleString(
									"de-DE"
								)} auswählen`}
				</Button>
			)}

			<Button
				size="sm"
				onClick={onDownloadZip}
				disabled={isZipping}
				aria-label="Auswahl als ZIP herunterladen"
				data-testid="archive-download-zip-button"
			>
				<Download className="mr-2 h-4 w-4" />
				{isZipping ? "Wird gepackt…" : "Als ZIP herunterladen"}
			</Button>

			{!isZipping && (
				<Button
					variant="ghost"
					size="sm"
					onClick={onClear}
					aria-label="Auswahl aufheben"
					data-testid="archive-clear-selection-button"
				>
					<X className="mr-1 h-4 w-4" />
					Aufheben
				</Button>
			)}

			{isZipping && progress && (
				<div className="flex min-w-[12rem] flex-1 items-center gap-2">
					<div
						className="h-1.5 flex-1 overflow-hidden rounded-full bg-background"
						role="progressbar"
						aria-valuenow={progress.percent}
						aria-valuemin={0}
						aria-valuemax={100}
					>
						<div
							className="h-full bg-primary transition-all"
							style={{ width: `${progress.percent}%` }}
						/>
					</div>
					<span className="shrink-0 text-xs text-muted-foreground">
						{progress.phase === "fetch"
							? `Lade ${progress.done}/${progress.total}`
							: `Packe ${progress.percent}%`}
					</span>
				</div>
			)}
		</div>
	);
};
